import React, { useContext, useEffect, useRef } from 'react';
import { Field, Formik } from 'formik';

import MiniAppEvents from 'src/helpers/miniapp-events';
import SpinEditReskin from 'src/components/spin-edit-reskin/spin-edit-reskin';
import { BriefcaseIcon } from 'src/assets/images';
import { vatPercent } from 'src/helpers/constants';
import {
  GetMinMaxCardFees,
  getMonthlyTotal,
  getOnceOffTotal,
  getAcquiringChannels,
} from './basket-utility';
import {
  SalesforceContext,
  AcquiringChannelFlags,
  LockedFieldNames,
} from 'src/context/salesforce-context';
import {
  formatCurrencyAmount,
  formatCurrencyAmountSimple,
  formatPercentageSimple,
} from 'src/helpers/formatting';
import { ShowIf } from 'src/components';
import toastMessageServiceSingleton from 'src/services/toast-message.service';
import TealiumEvents from 'src/helpers/tealium-events';
import { PRODUCT_IMAGE_MAP } from 'src/features/order/tech-debt/product-details-extra';
import './basket.scss';

export default function Basket(props) {
  const wrapperRef = useRef(null);
  const inMiniProgram = MiniAppEvents.isInMiniApp();
  const {
    merchant,
    setDeviceOrderQuantity,
    uniqueTerminals,
    customerType,
    isVodacomBusinessChannel,
    isFieldLocked,
    setAllDeliveriesInCategoryToZero,
    isProductCategory,
  } = useContext(SalesforceContext);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target) &&
        (!event?.target?.className?.split ||
          !event.target.className.split(' ').includes('toast-close-button'))
      ) {
        props.showBasket(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [wrapperRef]);

  const isQuantityFieldDisabled = isFieldLocked(
    LockedFieldNames.OPPORTUNITY_DELIVERIES_NUMBER_OF_DEVICES,
  );

  const getDeviceQuantitiesForFormik = () => {
    return Object.values(uniqueTerminals).reduce((accum, terminal) => {
      const deliveryInfo = merchant?.opportunity?.deliveries?.[terminal.id];
      const numberOfDevices = deliveryInfo ? deliveryInfo.number_of_devices : 0;
      const fieldName = `Quantity_Device_${terminal.id}`;
      accum[fieldName] = numberOfDevices;
      return accum;
    }, {});
  };

  const deviceQuantitySelectedLimit = 70;

  useEffect(() => {
    if (
      Object.values(merchant?.opportunity?.deliveries || {}).reduce(
        (acc, delivery) => acc + delivery.number_of_devices,
        0,
      ) > deviceQuantitySelectedLimit
    ) {
      toastMessageServiceSingleton.addError(
        'Maximum 70 terminals in total, please adjust your quantity.',
        null,
        'N/A',
        true,
      );
    }
  }, [merchant]);

  const onDeviceQuantityChange = (
    terminalId,
    numberOfDevices,
    price,
    isManualUpdate,
    oldQuantity,
    deviceName,
    productCategory,
  ) => {
    TealiumEvents.updateCart(customerType, deviceName, numberOfDevices > oldQuantity);
    setDeviceOrderQuantity(terminalId, numberOfDevices, price);
    if (numberOfDevices === 0 && isProductCategory(terminalId)) {
      setAllDeliveriesInCategoryToZero(productCategory);
    }
  };

  function deviceIdToQuantityFieldName(deviceId) {
    return `Quantity_Device_${deviceId}`;
  }

  const formatFees = (feeRange) =>
    `${formatPercentageSimple(feeRange[0])} - ${formatPercentageSimple(feeRange[1])}%`;

  const addVatToPrice = (price) => price * (1 + vatPercent);

  const deliveryList = Object.values(merchant?.opportunity?.deliveries || {})
    .filter(
      (delivery) =>
        delivery.number_of_devices > 0 &&
        (!delivery.product_category ||
          (!!delivery.product_category && delivery.is_product_category)),
    )
    .map((delivery) => ({
      numberOfDevices: delivery.number_of_devices,
      imgUrl: PRODUCT_IMAGE_MAP[delivery.device_name],
      price: delivery.price,
      name: delivery.device_name,
      feeRange: formatFees(GetMinMaxCardFees(delivery.device_id)),
      acquiring_channel: delivery.acquiring_channel,
      deviceId: delivery.device_id,
      product_category: delivery.product_category,
      maxQuantity: delivery.is_product_category ? 1 : deviceQuantitySelectedLimit,
    }));

  const isSingleDevice = deliveryList.length === 1;

  const desktopDevicesDisplay = (delivery) => (
    <div
      className={`columns desktop-columns is-vcentered is-variable is-1 ${
        delivery.acquiring_channel === AcquiringChannelFlags.vodapay ? 'vodapay-background' : ''
      }`}
      key={delivery.name}
      index={1}
    >
      {delivery.acquiring_channel !== AcquiringChannelFlags.vodapay ? (
        <>
          <div className="column is-2">
            <figure className="bgcolor-img">
              <img src={delivery.imgUrl} />
            </figure>
          </div>
          <div
            className={`column ${
              props.miniMode ? 'is-5' : 'is-3'
            } content-large-regular device-name`}
          >
            <div>
              {delivery.name}
              <div className="fee-subtext">{delivery.feeRange}</div>
            </div>
          </div>
          {!props.miniMode ? (
            <div className="column is-3">
              <Formik
                key={delivery.deviceId}
                initialValues={getDeviceQuantitiesForFormik()}
                enableReinitialize={true}
              >
                <Field
                  name={deviceIdToQuantityFieldName(delivery.deviceId)}
                  automationid={`Onboarding_SpinEdit_Quantity_${delivery.deviceId}`}
                  component={SpinEditReskin}
                  disabled={isQuantityFieldDisabled}
                  min={isSingleDevice ? 1 : 0}
                  max={delivery.maxQuantity}
                  onModified={(newNumber, isManualUpdate) =>
                    onDeviceQuantityChange(
                      delivery.deviceId,
                      newNumber,
                      delivery.price,
                      isManualUpdate,
                      delivery.numberOfDevices,
                      delivery.name,
                      delivery.product_category,
                    )
                  }
                  className="device-quantity-input light"
                />
              </Formik>
            </div>
          ) : (
            <div className="column is-1 has-text-right">x{delivery.numberOfDevices}</div>
          )}
          <div className={`column ${props.miniMode ? 'is-3' : 'is-3'} has-text-right`}>
            <ShowIf
              condition={
                !isVodacomBusinessChannel &&
                delivery.acquiring_channel !== AcquiringChannelFlags.buyInstoreFicaOnline
              }
            >
              <div className="content-large-regular payment-amount">
                R {formatCurrencyAmountSimple(addVatToPrice(delivery.price))}
              </div>
              <div className="light-text payment-frequency">
                {delivery.acquiring_channel === AcquiringChannelFlags.buyOnlineFicaOnline
                  ? ' / once-off'
                  : ' / month'}
              </div>
            </ShowIf>
            <ShowIf
              condition={delivery.acquiring_channel === AcquiringChannelFlags.buyInstoreFicaOnline}
            >
              <span className="content-large-regular">Pay in store</span>
            </ShowIf>
          </div>
        </>
      ) : (
        <>
          <div className="column is-2">
            <img className="vodapayImage" src={BriefcaseIcon} />
          </div>
          <div className="column is-9 content-large-regular device-name">
            <div>
              {delivery.name}
              <div className="fee-subtext">VodaPay Business Account</div>
            </div>
          </div>
        </>
      )}
      {!isSingleDevice && (
        <div
          className={`column is-1 close-cross icon-cross ${
            props.miniMode ? 'has-padding-left-6' : 'has-padding-left-5'
          }`}
          onClick={() => {
            setDeviceOrderQuantity(delivery.deviceId, 0, addVatToPrice(delivery.price));
            TealiumEvents.removeFromCart(customerType, delivery.name);
            if (isProductCategory(delivery.deviceId)) {
              setAllDeliveriesInCategoryToZero(delivery.product_category);
            }
          }}
        />
      )}
    </div>
  );

  const mobileDevicesDisplay = (delivery) => (
    <div
      className={`columns mobile-columns is-mobile is-vcentered is-variable is-1 ${
        delivery.acquiring_channel === AcquiringChannelFlags.vodapay ? 'vodapay-background' : ''
      }`}
      key={delivery.name + 'second'}
      index={2}
    >
      {delivery.acquiring_channel !== AcquiringChannelFlags.vodapay ? (
        <>
          <div className={`column ${props.miniMode ? 'is-3' : 'is-4'} device-name`}>
            <div>{delivery.name}</div>
          </div>
          {!props.miniMode ? (
            <div className="column is-4 has-text-centered">
              <figure className="is-inline-block">
                <Formik
                  key={delivery.deviceId}
                  initialValues={getDeviceQuantitiesForFormik()}
                  enableReinitialize={true}
                >
                  <Field
                    name={deviceIdToQuantityFieldName(delivery.deviceId)}
                    automationid={`Onboarding_SpinEdit_Quantity_${delivery.deviceId}`}
                    component={SpinEditReskin}
                    min={isSingleDevice ? 1 : 0}
                    max={delivery.maxQuantity}
                    disabled={isQuantityFieldDisabled}
                    onModified={(newNumber, isManualUpdate) =>
                      onDeviceQuantityChange(
                        delivery.deviceId,
                        newNumber,
                        delivery.price,
                        isManualUpdate,
                        delivery.numberOfDevices,
                        delivery.name,
                        delivery.product_category,
                      )
                    }
                    className="device-quantity-input light mobile-size"
                  />
                </Formik>
              </figure>
            </div>
          ) : (
            <div className="column is-4 has-text-centered">x{delivery.numberOfDevices}</div>
          )}
          <div className={`column ${props.miniMode ? 'is-4' : 'is-4'} has-text-right`}>
            <ShowIf
              condition={
                !isVodacomBusinessChannel &&
                delivery.acquiring_channel !== AcquiringChannelFlags.buyInstoreFicaOnline
              }
            >
              <span className="content-large-regular">
                R {formatCurrencyAmountSimple(addVatToPrice(delivery.price))}
              </span>
              <span className="light-text payment-frequency">
                {delivery.acquiring_channel === AcquiringChannelFlags.buyOnlineFicaOnline
                  ? ' once-off'
                  : ' pm'}
              </span>
            </ShowIf>
            <ShowIf
              condition={delivery.acquiring_channel === AcquiringChannelFlags.buyInstoreFicaOnline}
            >
              <span className="content-large-regular">Pay in store</span>
            </ShowIf>
          </div>
          {props.miniMode && !isSingleDevice && (
            <div
              className={`column is-1 close-cross icon-cross`}
              onClick={() => {
                setDeviceOrderQuantity(delivery.deviceId, 0, delivery.price);
                if (isProductCategory(delivery.deviceId)) {
                  setAllDeliveriesInCategoryToZero(delivery.product_category);
                }
                TealiumEvents.removeFromCart(customerType, delivery.name);
              }}
            />
          )}
        </>
      ) : (
        <>
          <div className="column is-2">
            <img className="vodapayImage" src={BriefcaseIcon} />
          </div>
          <div className="column is-9 content-large-regular device-name">
            <div>
              {delivery.name}
              <div className="fee-subtext">VodaPay Business Account</div>
            </div>
          </div>
        </>
      )}
      {delivery.acquiring_channel === AcquiringChannelFlags.vodapay && !isSingleDevice && (
        <div
          className={`column is-1 close-cross icon-cross`}
          onClick={() => {
            setDeviceOrderQuantity(delivery.deviceId, 0, delivery.price);
            if (isProductCategory(delivery.deviceId)) {
              setAllDeliveriesInCategoryToZero(delivery.product_category);
            }
            TealiumEvents.removeFromCart(customerType, delivery.name);
          }}
        />
      )}
    </div>
  );

  const onceOffTotal = getOnceOffTotal(merchant?.opportunity?.deliveries);
  const rentalTotal = getMonthlyTotal(merchant?.opportunity?.deliveries);
  const listOfAcquiringChannels = getAcquiringChannels(merchant?.opportunity?.deliveries);

  return (
    <div className={`BasketWrapper ${props.miniMode ? 'mini' : ''}`} ref={wrapperRef}>
      <div className="basket">
        <div
          className={`columns title-container is-fullwidth is-variable is-1 is-vcentered ${
            inMiniProgram ? 'in-mini-app' : ''
          }`}
        >
          <div className="column is-3 heading-six basket-heading">My basket</div>
          <div className="column is-8 is-hidden-mobile" />
          <div
            className={`column is-1 drop-down-closed-right-icon icon-drop-down-arrow-reskin close-basket ${
              props.miniMode ? 'mini-close-position' : 'has-padding-left-5'
            }`}
            onClick={() => props.showBasket(false)}
          />
        </div>
        <div className="devices-wrapper">
          {deliveryList.map((delivery) => desktopDevicesDisplay(delivery))}
          <div className="columns mobile-columns is-mobile is-vcentered is-variable is-1 is-hidden-desktop">
            <div className={`column ${props.miniMode ? 'is-3' : 'is-4'}`}>
              <p className="small-heading-text">Solution</p>
            </div>
            <div className="column is-4 has-text-centered">
              <p className="small-heading-text">QTY</p>
            </div>
            <div className="column is-4 has-text-right">
              <p className="small-heading-text">Amount</p>
            </div>
          </div>
          {deliveryList.map((delivery) => mobileDevicesDisplay(delivery))}
        </div>
        <div className="mobile-spacer" />
        <div
          className={`columns is-variable is-1 is-vcentered ${inMiniProgram ? 'in-mini-app' : ''}`}
        >
          <div className={`column ${props.miniMode ? 'is-5' : 'is-4'} once-off-total`}>
            <ShowIf
              condition={
                listOfAcquiringChannels.indexOf(AcquiringChannelFlags.buyOnlineFicaOnline) >= 0
              }
            >
              <p className="light-text-small grey-out once-off-text">Once-off + VAT</p>
              <p className="bold-total once-off-fee">{`R ${formatCurrencyAmount(onceOffTotal)}`}</p>
            </ShowIf>
          </div>
          <div className={`column ${props.miniMode ? 'is-1' : 'is-3'} is-hidden-mobile`} />
          <div className={`column ${props.miniMode ? 'is-5' : 'is-4'} monthly-total`}>
            <ShowIf
              condition={
                listOfAcquiringChannels.indexOf(AcquiringChannelFlags.rentOnlineFicaOnline) >= 0 ||
                listOfAcquiringChannels.indexOf(AcquiringChannelFlags.zeroRentOnlineFicaOnline) >= 0
              }
            >
              <p className="light-text-small grey-out right-align">Total monthly + VAT</p>
              <p className="bold-total right-align">{`R ${formatCurrencyAmount(rentalTotal)}`}</p>
            </ShowIf>
          </div>
          <div className="column is-1 is-hidden-mobile" />
        </div>
      </div>
    </div>
  );
}
